import { makeStyles } from '@material-ui/core/styles'
import { pageContentHorizontalGutters } from 'lib/styles/page-content-gutters'

const STATUS_TONES = {
  DRAFT: { backgroundColor: '#F4F6F8', color: '#4F5B62' },
  SCHEDULED: { backgroundColor: '#E0EAFF', color: '#001C57' },
  SENT: { backgroundColor: '#BEF1BB', color: '#00210B' },
  CANCELED: { backgroundColor: '#F4F6F8', color: '#6B7C87' },
}

const useStyles = makeStyles(theme => ({
  pageContent: {
    ...pageContentHorizontalGutters(theme),
    margin: theme.spacing(0, 0, 12),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  content: {
    margin: '2rem auto 6rem',
    maxWidth: 832,
    padding: theme.spacing(0, 2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 1),
      marginTop: theme.spacing(2),
    },
  },
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid #E0E0E0',
  },
  cardSubtitle: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  cardBody: {
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingRight: theme.spacing(2.5),
    color: 'inherit',
    '&:not(:last-child)': {
      borderBottom: '1px solid #E0E0E0',
    },
    '&:hover': {
      backgroundColor: '#FAFBFC',
    },
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
      paddingRight: 0,
    },
  },
  rowLink: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(2, 0, 2, 2.5),
    textAlign: 'left',
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      paddingRight: theme.spacing(2.5),
    },
    textDecoration: 'none',
    color: 'inherit',
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      padding: theme.spacing(0, 2.5, 2),
    },
  },
  borderedActionButton: {
    flexShrink: 0,
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowDetail: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  rowStats: {
    display: 'flex',
    gap: theme.spacing(2.5),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  rowStat: {
    textAlign: 'right',
  },
  rowStatValue: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: '#172024',
  },
  rowStatLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  statusBadge: ({ status }) => ({
    display: 'inline-block',
    flexShrink: 0,
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    ...(STATUS_TONES[status] || STATUS_TONES.DRAFT),
  }),
  pillButton: {
    borderRadius: 20,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  helperText: {
    fontSize: '0.8125rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  skillChip: {
    fontWeight: 500,
  },
  skillChipUnselected: {
    backgroundColor: 'transparent',
    border: '1px solid #70787C',
    color: '#40484C',
    '& .MuiChip-icon': {
      color: '#40484C',
    },
    '&:hover, &:focus': {
      backgroundColor: 'transparent',
    },
  },
  skillChipSelected: {
    backgroundColor: '#EBF0F2',
    border: '1px solid #EBF0F2',
    color: '#172024',
    '& .MuiChip-icon': {
      color: '#0053FF',
    },
    '&:hover, &:focus': {
      backgroundColor: '#EBF0F2',
    },
  },
  previewList: {
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  excluded: {
    color: theme.palette.text.disabled,
    textDecoration: 'line-through',
  },
  // One light grey for hover and keyboard focus; a selected option is shown by
  // its checkbox only.
  studentOptionRow: {
    minHeight: 56,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover, &[data-focus="true"], &:active': {
      backgroundColor: '#F4F6F8',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover, &[aria-selected="true"][data-focus="true"]': {
      backgroundColor: '#F4F6F8',
    },
  },
  filterOptionRow: {
    minHeight: 48,
    boxSizing: 'border-box',
    padding: theme.spacing(0.5, 1.5),
    '&:hover, &[data-focus="true"], &:active': {
      backgroundColor: '#F4F6F8',
    },
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover, &[aria-selected="true"][data-focus="true"]': {
      backgroundColor: '#F4F6F8',
    },
  },
  optionLabel: {
    fontSize: '0.875rem',
    color: '#172024',
  },
  selectedChip: {
    margin: theme.spacing(0.25),
    '& .MuiChip-deleteIcon': {
      width: 16,
      height: 16,
      marginRight: theme.spacing(0.75),
      color: '#40484C',
      '&:hover': {
        color: '#172024',
      },
    },
  },
  studentRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 56,
    boxSizing: 'border-box',
    padding: theme.spacing(1, 1.5),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  studentRowLabel: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    '& .MuiFormControlLabel-label': {
      minWidth: 0,
    },
  },
  mediaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  mediaTile: {
    position: 'relative',
    width: 112,
    height: 112,
    borderRadius: 8,
    border: '1px solid #E0E0E0',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: '#FAFBFC',
    textAlign: 'center',
  },
  mediaImage: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mediaTitle: {
    fontSize: '0.75rem',
    lineHeight: 1.3,
    color: '#172024',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  mediaRemove: {
    position: 'absolute',
    top: 4,
    right: 4,
    padding: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
  },
  mediaActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  actionBarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  fieldSection: {
    width: '100%',
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  sectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  sectionTitle: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  sectionTitleWithHelper: {
    marginBottom: theme.spacing(0.5),
  },
  sectionHelper: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  dateTimeFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'start',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  cardStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  listStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  chatPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    '& a': {
      pointerEvents: 'none',
    },
  },
  chatBubble: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    boxSizing: 'border-box',
    width: 'min(100%, 375px)',
    padding: theme.spacing(0.5),
    borderRadius: '18px 18px 6px 18px',
    backgroundColor: '#E0EAFF',
    color: '#111111',
    fontSize: '0.9375rem',
    lineHeight: 1.35,
  },
  chatBubbleWithAttachments: {
    padding: theme.spacing(1),
  },
  chatText: {
    overflowWrap: 'break-word',
    '& p': {
      margin: 0,
    },
    '& ul, & ol': {
      margin: 0,
      paddingLeft: theme.spacing(2.5),
    },
    '& a': {
      color: '#0053FF',
    },
    fontSize: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    padding: theme.spacing(0.75, 1.25),
  },
  chatImage: {
    width: '100%',
    borderRadius: 8,
    display: 'block',
  },
  chatFile: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.common.white,
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  chatFileIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  chatFileName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#172024',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  statCard: {
    padding: theme.spacing(1.75, 2),
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#00205B',
  },
  statLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#172024',
  },
  statCaption: {
    marginTop: theme.spacing(0.25),
    fontSize: '0.75rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  directTone: {
    borderColor: '#9FD89B',
    backgroundColor: '#F2FBF1',
  },
  influencedTone: {
    borderColor: '#C5D4FF',
    backgroundColor: '#F4F7FF',
  },
  footnote: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  table: {
    '& th': {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
    },
    '& td': {
      fontSize: '0.8125rem',
    },
  },
  kindBadge: ({ kind }) => ({
    display: 'inline-block',
    fontFamily: theme.typography.fontFamily,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.25, 1),
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 600,
    ...(kind === 'DIRECT'
      ? { backgroundColor: '#BEF1BB', color: '#00210B' }
      : { backgroundColor: '#E0EAFF', color: '#001C57' }),
  }),
}))

export default useStyles
